// origin: WS_DeskOS artifacts/edge-tracker/src/lib/api/http.ts (one client, ApiError with the server's code; bearer token
// replaced by the cookie session + CSRF header)
/**
 * The one HTTP client of the SPA. Cookies carry the session (httpOnly); mutations carry the session's CSRF token, which
 * the client learns from /me and from sign-in answers. Errors become ApiError with the server's code and its message,
 * already translated (X-Lang).
 */
import type { Lang } from "@prop/i18n";
import { deviceHeaders } from "./device";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;
const DEVICE_PATHS = /^\/(auth\/(register|login|telegram\/poll|mfa\/verify)|orders)$/;
let lang: Lang = "en";

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}
export function setApiLang(l: Lang): void {
  lang = l;
}

function remember(json: unknown): void {
  const j = json as { csrfToken?: unknown; me?: { csrfToken?: unknown } } | null;
  const token = j?.me?.csrfToken ?? j?.csrfToken;
  if (typeof token === "string") csrfToken = token;
}

export async function api<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", "X-Lang": lang };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;
  // ADR-0044: hashed device marks on sign-up, sign-in and checkout only (fraud prevention: one person, one profile).
  if (method === "POST" && DEVICE_PATHS.test(path)) Object.assign(headers, await deviceHeaders());
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, credentials: "same-origin", body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "NETWORK", "");
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, json?.code ?? "INTERNAL", json?.error ?? "", json?.details);
  remember(json);
  return json as T;
}

export const get = <T>(p: string) => api<T>("GET", p);
export const post = <T>(p: string, b: unknown = {}) => api<T>("POST", p, b);
export const put = <T>(p: string, b: unknown = {}) => api<T>("PUT", p, b);
export const patch = <T>(p: string, b: unknown = {}) => api<T>("PATCH", p, b);
export const del = <T>(p: string) => api<T>("DELETE", p);
